import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

/**
 * Reads only contacts.next_contact_interval_days.
 *
 * Deliberately a direct one-column select rather than going through
 * contacts_with_primary_details: that view enumerates its columns explicitly, so a new base
 * column is invisible through it, and recreating it means the full dependent drop-order
 * dance on an object that is already stale (missing 12 base columns). One tiny round-trip
 * keeps the two riskiest objects in this subsystem untouched.
 *
 * Returns null both while loading and when the contact has no override. Callers pass the
 * result straight to resolveCadenceDays(), which maps null to the system default, so the
 * pre-load render shows the default rather than flickering an empty state.
 *
 * The key nests under queryKeys.contacts.all, so the existing
 * invalidateQueries({ queryKey: queryKeys.contacts.all }) after a logged call refreshes it.
 */
export function useContactInterval(
  contactId?: string,
  options?: { enabled?: boolean },
) {
  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.contacts.detail(contactId ?? ''), 'interval'] as const,
    // `enabled` is not optional sugar. ContactsTableColumns mounts a SkipContactDialog for
    // EVERY row unconditionally, so without a caller-supplied gate this hook fires one query
    // per row on the (virtualized, perf-sensitive) contacts list just to prefill a dialog
    // nobody opened. Callers that render eagerly must pass { enabled: open }.
    enabled: Boolean(contactId) && (options?.enabled ?? true),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('next_contact_interval_days')
        .eq('id', contactId!)
        .single();
      if (error) throw error;
      return (data?.next_contact_interval_days ?? null) as number | null;
    },
  });

  return { intervalDays: data ?? null, isLoading };
}
